import React from 'react'
import { useDragLayer } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import Task from '../../routes/Client-Portal/FinancialStory/advisor-components/task-components/Task'
import ChapterItem from '../../routes/Client-Portal/FinancialStory/advisor-components/ChapterItem'
import ChapterGoal from '../../routes/Client-Portal/FinancialStory/advisor-components/chapter-components/ChapterGoal'
import CustomFolderPreview from '../fileManagement/components/DocumentManager/customDragPreviews/CusomFolderPreview'
import CustomFilePreview from '../fileManagement/components/DocumentManager/customDragPreviews/CustomFilePreview'

/** Fixed full-screen overlay style used as the drag layer canvas; pointer-events disabled so it doesn't intercept clicks. */
const layerStyles = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 100,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
}

/**
 * Pixel offsets applied to each item type's drag preview so the preview is
 * visually offset from the cursor rather than anchored at the top-left corner.
 * @type {Record<string, { x: number, y: number }>}
 */
const ITEM_OFFSETS = {
    task: {
        x: 680,
        y: 20,
    },
    chapter: {
        x: 215,
        y: 33,
    },
    goal: {
        x: 700,
        y: 25,
    },
    folder: {
        x: 100,
        y: 33,
    },
    file: {
        x: 100,
        y: 33,
    },
};

/**
 * Returns the Y-axis pixel offset for a given drag item type.
 * @param {string} itemType - The drag item type key.
 * @returns {number} The Y offset in pixels, or 0 if the type is not in ITEM_OFFSETS.
 */
const getYOffset = (itemType) => ITEM_OFFSETS[itemType]?.y ?? 0;

/**
 * Returns the X-axis pixel offset for a given drag item type.
 * @param {string} itemType - The drag item type key.
 * @returns {number} The X offset in pixels, or 0 if the type is not in ITEM_OFFSETS.
 */
const getXOffset = (itemType) => ITEM_OFFSETS[itemType]?.x ?? 0;
/**
 * Computes the CSS transform style for the drag preview element.
 * Applies item-type-specific offsets so the preview appears correctly positioned relative to the cursor.
 * @param {string} itemType - The drag item type key.
 * @param {{ x: number, y: number }|null} currentOffset - The current cursor offset from react-dnd.
 * @returns {{ display: 'none' } | { transform: string, WebkitTransform: string }} Hidden style when not dragging, or translate transform style.
 */
const getItemStyles = (itemType, currentOffset) => {
    const initialOffsetX = getXOffset(itemType)
    const initialOffsetY = getYOffset(itemType)

    if (!currentOffset) {
        return {
            display: 'none',
        }
    }
    const x = currentOffset.x - initialOffsetX
    const y = currentOffset.y - initialOffsetY

    const transform = `translate(${x}px, ${y}px)`
    return {
        transform,
        WebkitTransform: transform,
    }
}

/**
 * CustomDragLayer
 *
 * A React DnD custom drag layer that renders a floating preview of the dragged item.
 * Subscribes to useDragLayer to get the current drag item, its offset and dragging state,
 * and conditionally renders a preview component for supported item types.
 *
 * @returns {JSX.Element|null} A fixed-position preview layer while dragging, or null when not dragging.
 */
const CustomDragLayer = () => {
    const { isDragging, item, currentOffset } = useDragLayer(
        (monitor) => ({
            item: monitor.getItem(),
            initialOffset: monitor.getInitialSourceClientOffset(),
            currentOffset: monitor.getSourceClientOffset(),
            isDragging: monitor.isDragging(),
        })
    )

/**
 * 
 * renderItem

 * Renders the appropriate drag preview element for a given drag item.
 *
 * @param {Object} item - The drag item object returned by react-dnd's monitor.getItem().
 * @param {string} item.type - The item type identifier (e.g. 'task', 'chapter', 'goal', 'folder', 'file', or NativeTypes.FILE).
 * @param {Object} [item.content] - The original component's props container; may be undefined for native file drops.
 * @param {Object} [item.content.props] - The props passed to the original component (contains task, chapter, goal, folder or file data and query keys).
 * @returns {JSX.Element|null} A React element to display as the drag preview for supported types, or null for native file drops or unsupported types. 
 */
    const renderItem = (item) => {
        // Handle native file drops
        if (item.type === NativeTypes.FILE || !item.content) {
            return null;
        }

        const props = item.content.props
        if (item.type === 'task') {
            return (
                <div className="max-w-screen-md rotate-[2deg] transition duration-300 ease-in-out">
                    <Task task={props.task}
                        tasksListQueryKey={props.tasksListQueryKey}
                        chaptersListQueryKey={props.chaptersListQueryKey}
                        isMeetingMode={props.isMeetingMode} />
                </div>
            )
        }
        if (item.type === 'chapter') {
            return (
                <div className="max-w-[250px] rotate-[2deg] transition duration-300 ease-in-out">
                    <ChapterItem
                        selectedChapterId={props.selectedChapterId}
                        setSelectedChapterId={props.setSelectedChapterId}
                        chapter={props.chapter}
                    />
                </div>
            )
        }
        if (item.type === 'goal') {
            return (
                <div className="max-w-screen-md rotate-[2deg] transition duration-300 ease-in-out">
                    <ChapterGoal goal={props.goal} queryKeys={props.queryKeys} />
                </div>
            )
        }
        if (item.type === 'folder') {
            return (
                <div className="max-w-[250px] rotate-[2deg] transition duration-300 ease-in-out">
                    <CustomFolderPreview folder={props.folder} />
                </div>
            );
        }
        if (item.type === 'file') {
            return (
                <div className="max-w-[250px] rotate-[2deg] transition duration-300 ease-in-out">
                    <CustomFilePreview file={props.file} />
                </div>
            );
        }
    }

    if (!isDragging) {
        return null
    }

    return (
        <div style={layerStyles}>
            <div style={getItemStyles(item.type, currentOffset)}>
                {renderItem(item)}
            </div>
        </div>
    )
}

export default CustomDragLayer
